import { useEffect, useState, type FormEvent } from 'react';
import { money, dateLabel } from './api';
import { usePageMeta } from './seo';
import { usePwaInstall } from './pwa';

const KASA_KEY = 'kasa_session';

function getKasaToken(): string | null {
  try { return localStorage.getItem(KASA_KEY) || sessionStorage.getItem(KASA_KEY) || null; } catch { return null; }
}
function setKasaToken(token: string | null, remember = true) {
  try {
    localStorage.removeItem(KASA_KEY);
    sessionStorage.removeItem(KASA_KEY);
    if (token) (remember ? localStorage : sessionStorage).setItem(KASA_KEY, token);
  } catch { /* yoksay */ }
}
async function kasaApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getKasaToken();
  const res = await fetch(`/api/kasa${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { message?: string }).message || 'İşlem tamamlanamadı.');
  return data as T;
}

// Yerel (Istanbul) bugün — toISOString UTC olur ve gece geç saatte günü kaydırabilir.
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type KasaEmployee = { id: string; full_name: string };
type KasaService = { id: string; name: string; price: string; duration_minutes: number; is_vip: boolean };
type KasaCustomer = { first_name: string; last_name: string; phone: string };
type KasaAppt = { id: string; time: string; status: string; firstName: string; lastName: string; phone: string; serviceId: string; serviceName: string; price: string; employeeId: string; employeeName: string };
const emptyQuick = () => ({ firstName: '', lastName: '', phone: '', serviceId: '', employeeId: '', date: localToday(), time: '' });

export default function KasaApp() {
  usePageMeta('Kasa | Rastamen Lounge');
  const [token, setToken] = useState<string | null>(getKasaToken());
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');

  // Kasa subdomain'i: kendi PWA kimliği (isim/manifest) + arama motorlarına kapalı.
  useEffect(() => {
    const setMeta = (name: string, content: string) => {
      let m = document.head.querySelector(`meta[name="${name}"]`);
      if (!m) { m = document.createElement('meta'); m.setAttribute('name', name); document.head.appendChild(m); }
      const prev = m.getAttribute('content');
      m.setAttribute('content', content);
      return () => { if (prev !== null) m!.setAttribute('content', prev); };
    };
    const restoreRobots = setMeta('robots', 'noindex, nofollow');
    const restoreTitle = setMeta('apple-mobile-web-app-title', 'Rastamen Kasa');
    const manifest = document.head.querySelector('link[rel="manifest"]');
    const prevManifest = manifest?.getAttribute('href') || null;
    if (manifest) manifest.setAttribute('href', '/kasa.webmanifest');
    document.body.classList.add('kasa-body');
    return () => {
      restoreRobots(); restoreTitle();
      document.body.classList.remove('kasa-body');
      if (manifest && prevManifest) manifest.setAttribute('href', prevManifest);
    };
  }, []);

  const login = async (e: FormEvent) => {
    e.preventDefault();
    setAuthBusy(true); setAuthError('');
    try {
      const res = await fetch('/api/kasa/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password, remember }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { message?: string }).message || 'Giriş başarısız.');
      const newToken = (data as { token: string }).token;
      setKasaToken(newToken, remember);
      setToken(newToken);
      setPassword('');
    } catch (err) { setAuthError((err as Error).message); } finally { setAuthBusy(false); }
  };

  if (!token) {
    return (
      <div className="kasa-gate">
        <form className="kasa-login" onSubmit={login}>
          <div className="kasa-brand"><img src="/rastamen-logo.png" alt="" /><span>KASA</span></div>
          <h1>Kasa Girişi</h1>
          <p>Devam etmek için kasa şifresini girin.</p>
          {authError && <div className="kasa-error">{authError}</div>}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Kasa şifresi" autoFocus required />
          <label className="kasa-remember"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Bu cihazda hatırla</label>
          <button type="submit" className="button primary" disabled={authBusy || !password}>{authBusy ? 'Kontrol ediliyor...' : 'Giriş Yap'}</button>
        </form>
      </div>
    );
  }

  return <KasaDashboard onLogout={() => { setKasaToken(null); setToken(null); }} />;
}

function KasaDashboard({ onLogout }: { onLogout: () => void }) {
  const { canInstall, promptInstall, standalone, ios } = usePwaInstall();
  const [date, setDate] = useState(localToday());
  const [employees, setEmployees] = useState<KasaEmployee[]>([]);
  const [services, setServices] = useState<KasaService[]>([]);
  const [error, setError] = useState('');

  // Müşteri
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<KasaCustomer[]>([]);

  // Randevu entegrasyonu
  const [appointments, setAppointments] = useState<KasaAppt[]>([]);
  const [showAppts, setShowAppts] = useState(false);
  const [linkedAppt, setLinkedAppt] = useState<{ id: string; label: string; time: string } | null>(null);
  const [showQuick, setShowQuick] = useState(false);
  const [quick, setQuick] = useState(emptyQuick());
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickError, setQuickError] = useState('');

  // Satış
  const [employeeId, setEmployeeId] = useState('');
  const [selectedSvc, setSelectedSvc] = useState<Set<string>>(new Set());
  const [total, setTotal] = useState('');
  const [extra, setExtra] = useState('');
  const [showExtra, setShowExtra] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [confirmSale, setConfirmSale] = useState(false);
  // Sadakat (VARSAYILAN KAPALI). Kapalıyken loyaltyOn=false → hiç sorgulanmaz, hiçbir şey görünmez.
  const [loyaltyOn, setLoyaltyOn] = useState(false);
  const [loyalty, setLoyalty] = useState<{ enabled: boolean; nextVisit?: number; visits?: number; percent?: number; label?: string; qualifies?: boolean } | null>(null);

  const loadAppts = () => { kasaApi<KasaAppt[]>(`/appointments?date=${date}`).then(setAppointments).catch(() => setAppointments([])); };
  useEffect(() => { loadAppts(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [date]);
  useEffect(() => { kasaApi<KasaEmployee[]>('/employees').then(setEmployees).catch(() => undefined); }, []);
  useEffect(() => { kasaApi<KasaService[]>('/services').then(setServices).catch(() => undefined); }, []);
  // Açık mı? Tek sefer öğren. Kapalıysa aşağıdaki telefon-sorgusu hiç çalışmaz.
  useEffect(() => { kasaApi<{ enabled: boolean }>('/loyalty').then((r) => setLoyaltyOn(Boolean(r.enabled))).catch(() => setLoyaltyOn(false)); }, []);
  useEffect(() => {
    if (!loyaltyOn || phone.replace(/\D/g, '').length < 10) { setLoyalty(null); return; }
    const h = window.setTimeout(() => {
      kasaApi<typeof loyalty>(`/loyalty?phone=${encodeURIComponent(phone.trim())}`).then(setLoyalty).catch(() => setLoyalty(null));
    }, 300);
    return () => window.clearTimeout(h);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [phone, loyaltyOn]);

  // Müşteri otomatik tamamlama (yazarken, gecikmeli).
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const h = window.setTimeout(() => {
      kasaApi<KasaCustomer[]>(`/customers?q=${encodeURIComponent(query.trim())}`).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => window.clearTimeout(h);
  }, [query]);

  const flashMsg = (msg: string, ms = 1900) => { setFlash(msg); window.setTimeout(() => setFlash(''), ms); };

  const selectCustomer = (c: KasaCustomer) => {
    setName(`${c.first_name || ''} ${c.last_name || ''}`.trim());
    setPhone(c.phone || '');
    setQuery(''); setResults([]);
  };

  const pickAppt = (a: KasaAppt) => {
    setName(`${a.firstName || ''} ${a.lastName || ''}`.trim());
    setPhone(a.phone || '');
    setEmployeeId(a.employeeId || '');
    setSelectedSvc(new Set(a.serviceId ? [a.serviceId] : []));
    setTotal(Number(a.price) ? String(Number(a.price)) : '');
    setLinkedAppt({ id: a.id, label: `${a.firstName} ${a.lastName}`.trim(), time: a.time });
    setShowAppts(false); setResults([]); setQuery(''); setError('');
  };

  const toggleSvc = (id: string) => {
    const next = new Set(selectedSvc);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedSvc(next);
    const sum = services.filter((s) => next.has(s.id)).reduce((a, s) => a + Number(s.price), 0);
    setTotal(sum ? String(sum) : '');
  };

  const totalNum = Number(String(total || '0').replace(',', '.'));       // hizmet/taban tutar
  const extraNum = Number(String(extra || '0').replace(',', '.'));       // ekstra ödeme (bahşiş/ek)
  // Sadakat indirimi OTOMATIK türetilir (total'a DOKUNULMAZ → çift uygulama yok, hizmet
  // değişince kendiliğinden yeniden hesaplanır). Kapalı/uygun değilse 0 → grandNum aynı kalır.
  const loyaltyDiscount = loyalty?.qualifies && loyalty.percent ? Math.round(totalNum * loyalty.percent / 100) : 0;
  const grandNum = totalNum + extraNum - loyaltyDiscount;                // işlenecek toplam (indirimli)

  const resetSale = () => {
    setName(''); setPhone(''); setQuery(''); setResults([]);
    setSelectedSvc(new Set()); setTotal(''); setExtra(''); setShowExtra(false); setEmployeeId(''); setNote(''); setLinkedAppt(null);
  };

  const save = async () => {
    if (!(grandNum > 0)) { setError('Tutar girin veya bir hizmet seçin.'); return; }
    setSaving(true); setError('');
    try {
      if (linkedAppt) {
        // Randevu tamamlama: hizmet + ekstra TEK kayit (amount=grandNum) → çoklama olmaz.
        await kasaApi(`/appointments/${linkedAppt.id}/complete`, { method: 'POST', body: JSON.stringify({ amount: grandNum }) });
      } else {
        const svcNames = services.filter((s) => selectedSvc.has(s.id)).map((s) => s.name);
        const category = svcNames[0] || 'Kasa Satışı';
        const cust = [name.trim(), phone.trim() ? `(${phone.trim()})` : ''].filter(Boolean).join(' ');
        const desc = [cust, svcNames.join(', '), note.trim(), extraNum > 0 ? `+ ${money(extraNum)} ekstra` : '', loyaltyDiscount > 0 ? `− %${loyalty?.percent} ${loyalty?.label || 'sadakat'} (−${money(loyaltyDiscount)})` : ''].filter(Boolean).join(' — ');
        await kasaApi('/entry', { method: 'POST', body: JSON.stringify({ type: 'income', amount: grandNum, category, description: desc, employeeId: employeeId || null, entryDate: date, customerName: name.trim() || null, customerPhone: phone.trim() || null }) });
      }
      flashMsg(`${money(grandNum)} kasaya işlendi ✓`);
      setConfirmSale(false);
      resetSale();
      loadAppts();
    } catch (err) { setError((err as Error).message); } finally { setSaving(false); }
  };

  const submitQuick = async () => {
    if (!quick.serviceId || !quick.employeeId || !quick.firstName.trim() || !quick.phone.trim() || !quick.time) {
      setQuickError('Hizmet, personel, ad, telefon ve saat gerekli.'); return;
    }
    setQuickBusy(true); setQuickError('');
    try {
      await kasaApi('/appointments', { method: 'POST', body: JSON.stringify(quick) });
      setShowQuick(false); setQuick(emptyQuick());
      flashMsg('Randevu eklendi ✓', 1800);
      loadAppts();
    } catch (err) { setQuickError((err as Error).message); } finally { setQuickBusy(false); }
  };

  const installApp = async () => {
    if (canInstall) { await promptInstall(); }
    else if (ios) { flashMsg('Safari: Paylaş ⬆ → "Ana Ekrana Ekle"', 3200); }
  };

  const isToday = date === localToday();

  return (
    <div className="kasa-shell kasa-fit">
      <header className="kasa-header">
        <div className="kasa-brand"><img src="/rastamen-logo.png" alt="" /><span>KASA</span></div>
        <div className="kasa-header-actions">
          <input type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value || localToday())} />
          {!standalone && (canInstall || ios) && <button type="button" className="kasa-install" onClick={installApp}>⤓ Yükle</button>}
          <button type="button" className="kasa-logout" onClick={onLogout}>Çıkış</button>
        </div>
      </header>

      {error && <div className="kasa-error">{error}</div>}

      <div className="kasa-pos">
        {/* ÜST SOL — Müşteri */}
        <div className="kasa-pos-cust">
          <div className="kasa-col-head">
            <h3>Müşteri</h3>
            <div className="kasa-cust-actions">
              <button type="button" onClick={() => { loadAppts(); setShowAppts((v) => !v); }}>◷ Randevular{appointments.length ? ` (${appointments.length})` : ''}</button>
              <button type="button" onClick={() => { setQuick({ ...emptyQuick(), date }); setQuickError(''); setShowQuick(true); }}>＋ Telefonla</button>
            </div>
          </div>

          {showAppts && (
            <div className="kasa-appt-list">
              {appointments.length === 0 ? <p className="kasa-empty">{isToday ? 'Bugün' : dateLabel(date)} için randevu yok.</p> : appointments.map((a) => (
                <button type="button" key={a.id} onClick={() => pickAppt(a)}>
                  <b>{a.time}</b>
                  <span className="kasa-appt-cust"><strong>{a.firstName} {a.lastName}</strong><small>{a.serviceName} · {a.employeeName}</small></span>
                  <span className="kasa-appt-price">{money(a.price)}</span>
                </button>
              ))}
            </div>
          )}

          {linkedAppt && (
            <div className="kasa-linked">
              <span>◷ {linkedAppt.label} · {linkedAppt.time}</span>
              <button type="button" onClick={() => setLinkedAppt(null)} aria-label="Randevu bağlantısını kaldır">×</button>
            </div>
          )}

          <div className="kasa-cust-fields">
            <label className="kasa-field">Ad Soyad
              <input value={name} onChange={(e) => { setName(e.target.value); setQuery(e.target.value); }} placeholder="Müşteri adı" autoComplete="off" />
            </label>
            <label className="kasa-field">Telefon
              <input value={phone} onChange={(e) => { setPhone(e.target.value); setQuery(e.target.value); }} placeholder="05xx xxx xx xx" inputMode="tel" autoComplete="off" />
            </label>
          </div>
          {results.length > 0 && (
            <ul className="kasa-cust-results">
              {results.map((c, i) => (
                <li key={i}><button type="button" onClick={() => selectCustomer(c)}><strong>{c.first_name} {c.last_name}</strong><small>{c.phone}</small></button></li>
              ))}
            </ul>
          )}
        </div>

        {/* ÜST SAĞ — Satış bilgisi */}
        <div className="kasa-pos-sale">
          <div className="kasa-sale-row">
            <label className="kasa-field">Personel <small>ops.</small>
              <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                <option value="">—</option>
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
              </select>
            </label>
            <label className="kasa-field kasa-total">Tutar
              <div><span>₺</span><input inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value.replace(/[^\d.,]/g, ''))} placeholder="0" autoComplete="off" /></div>
            </label>
          </div>
          {(showExtra || extraNum > 0) ? (
            <label className="kasa-field kasa-extra-field">Ekstra ödeme <small>bahşiş / ek tutar</small>
              <div><span>＋₺</span><input inputMode="decimal" value={extra} onChange={(e) => setExtra(e.target.value.replace(/[^\d.,]/g, ''))} placeholder="0" autoComplete="off" autoFocus /><button type="button" className="kasa-extra-clear" onClick={() => { setExtra(''); setShowExtra(false); }} aria-label="Ekstra ödemeyi kaldır">×</button></div>
            </label>
          ) : (
            <button type="button" className="kasa-extra-add" onClick={() => setShowExtra(true)}>＋ Ekstra ödeme ekle</button>
          )}
          <label className="kasa-field kasa-note-field">Not <small>ops.</small><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Açıklama" autoComplete="off" /></label>
          {loyalty?.qualifies && <div className="kasa-loyalty"><div><strong>★ {loyalty.label} · %{loyalty.percent}</strong><small>{loyalty.nextVisit}. ziyaret — {loyaltyDiscount > 0 ? `otomatik uygulandı (−${money(loyaltyDiscount)})` : 'hizmet/tutar seçilince uygulanır'}</small></div></div>}
        </div>

        {/* ALT — Hizmetler (boydan boya) */}
        <div className="kasa-services-wrap">
          <div className="kasa-svc-head"><span>Hizmetler</span><small>seç, tutar otomatik hesaplansın</small></div>
          <div className="kasa-services">
            {services.map((s) => (
              <button type="button" key={s.id} className={`kasa-svc${selectedSvc.has(s.id) ? ' active' : ''}${s.is_vip ? ' vip' : ''}`} onClick={() => toggleSvc(s.id)}>
                <strong>{s.name}</strong><span>{money(s.price)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="kasa-save-bar">
        <div className="kasa-save-sum">
          {linkedAppt ? <span>◷ {linkedAppt.label}</span> : name.trim() ? <span>{name.trim()}</span> : <span className="muted">Müşteri seçilmedi</span>}
          <strong>{money(grandNum)}</strong>
          {(extraNum > 0 || loyaltyDiscount > 0) && <small className="kasa-save-extra">hizmet {money(totalNum)}{loyaltyDiscount > 0 ? ` − ${money(loyaltyDiscount)} sadakat` : ''}{extraNum > 0 ? ` + ${money(extraNum)} ekstra` : ''}</small>}
        </div>
        <button type="button" className="kasa-save" disabled={saving || !(grandNum > 0)} onClick={() => { setError(''); setConfirmSale(true); }}>
          {linkedAppt ? 'Ödemeyi Al' : 'Kasaya İşle'}
        </button>
      </div>

      {confirmSale && (
        <div className="kasa-modal" onClick={() => { if (!saving) setConfirmSale(false); }}>
          <div className="kasa-modal-card kasa-confirm" role="dialog" aria-modal="true" aria-label="Kasaya işleme onayı" onClick={(e) => e.stopPropagation()}>
            <div className="kasa-modal-head"><h3>{linkedAppt ? 'Ödeme alınsın mı?' : 'Kasaya işlensin mi?'}</h3><button type="button" onClick={() => setConfirmSale(false)} disabled={saving} aria-label="Kapat">×</button></div>
            {error && <div className="kasa-error">{error}</div>}
            <div className="kasa-confirm-amount"><small>İşlenecek tutar</small><strong>{money(grandNum)}</strong>{(extraNum > 0 || loyaltyDiscount > 0) && <span className="kasa-confirm-extra">hizmet {money(totalNum)}{loyaltyDiscount > 0 ? ` − ${money(loyaltyDiscount)} sadakat` : ''}{extraNum > 0 ? ` + ${money(extraNum)} ekstra` : ''}</span>}</div>
            <div className="kasa-confirm-lines">
              {(linkedAppt || name.trim()) && <div><span>Müşteri</span><b>{linkedAppt ? linkedAppt.label : name.trim()}</b></div>}
              {linkedAppt && <div><span>Randevu saati</span><b>{linkedAppt.time}</b></div>}
              {!linkedAppt && selectedSvc.size > 0 && <div><span>Hizmet</span><b>{services.filter((s) => selectedSvc.has(s.id)).map((s) => s.name).join(', ')}</b></div>}
              {employeeId && <div><span>Personel</span><b>{employees.find((e) => e.id === employeeId)?.full_name || '—'}</b></div>}
            </div>
            <div className="kasa-confirm-actions">
              <button type="button" className="kasa-confirm-cancel" onClick={() => setConfirmSale(false)} disabled={saving}>Vazgeç</button>
              <button type="button" className="kasa-save" onClick={save} disabled={saving}>{saving ? 'İşleniyor…' : linkedAppt ? 'Evet, ödemeyi al' : 'Evet, işle'}</button>
            </div>
          </div>
        </div>
      )}

      {showQuick && (
        <div className="kasa-modal" onClick={() => setShowQuick(false)}>
          <div className="kasa-modal-card" role="dialog" aria-modal="true" aria-label="Telefonla randevu ekle" onClick={(e) => e.stopPropagation()}>
            <div className="kasa-modal-head"><h3>Telefonla Randevu Ekle</h3><button type="button" onClick={() => setShowQuick(false)} aria-label="Kapat">×</button></div>
            {quickError && <div className="kasa-error">{quickError}</div>}
            <div className="kasa-qa-grid">
              <label className="kasa-field">Ad<input value={quick.firstName} onChange={(e) => setQuick({ ...quick, firstName: e.target.value })} autoFocus /></label>
              <label className="kasa-field">Soyad<input value={quick.lastName} onChange={(e) => setQuick({ ...quick, lastName: e.target.value })} /></label>
              <label className="kasa-field">Telefon<input value={quick.phone} onChange={(e) => setQuick({ ...quick, phone: e.target.value })} inputMode="tel" placeholder="05xx xxx xx xx" /></label>
              <label className="kasa-field">Personel<select value={quick.employeeId} onChange={(e) => setQuick({ ...quick, employeeId: e.target.value })}><option value="">Seçin</option>{employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}</select></label>
              <label className="kasa-field kasa-qa-full">Hizmet<select value={quick.serviceId} onChange={(e) => setQuick({ ...quick, serviceId: e.target.value })}><option value="">Seçin</option>{services.map((s) => <option key={s.id} value={s.id}>{s.name} · {money(s.price)}</option>)}</select></label>
              <label className="kasa-field">Tarih<input type="date" value={quick.date} onChange={(e) => setQuick({ ...quick, date: e.target.value })} /></label>
              <label className="kasa-field">Saat<input type="time" value={quick.time} onChange={(e) => setQuick({ ...quick, time: e.target.value })} /></label>
            </div>
            <button type="button" className="kasa-save" disabled={quickBusy} onClick={submitQuick}>{quickBusy ? 'Ekleniyor…' : 'Randevuyu Ekle'}</button>
          </div>
        </div>
      )}

      {flash && <div className="kasa-flash" role="status">{flash}</div>}
    </div>
  );
}
